// PURPOSE: Edit Home in place: drag, resize, hide and add panels on the live page.
// CONTRACT: Spec 059 item 5 (amended 2026-10-09); the parent saves with the observed layout version.
// DEPENDS: HomePage owns the draft and the save; panels keep rendering their real content.
// CEILING: Available panels are server hints, never authorization.
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject } from 'react';
import { ArrowUp, ArrowDown, EyeOff, GripVertical, Plus } from 'lucide-react';
import { ErrorNotice } from './components';
import type { HomePanel, HomePanelOption } from './types';

const EDGE = 72;
type Drag = { started: number; originX: number; originY: number; active: boolean; key: string; id: number; grabX: number; grabY: number; x: number; y: number; dx: number; dy: number; start: string[]; last?: string };
const slots = (grid: HTMLElement | null) => Array.from(grid?.querySelectorAll<HTMLElement>(':scope > [data-panel]') || []);
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function moveKey(keys: string[], key: string, to: number) {
 const next = keys.filter(k => k !== key); next.splice(Math.max(0, Math.min(to, next.length)), 0, key); return next;
}

// Pointer drag with live reflow: the dragged panel follows the finger, the others slide (FLIP) into place.
export function useReorder(grid: RefObject<HTMLDivElement | null>, keys: string[], reorder: (next: string[]) => void, describe: (key: string, index: number) => void) {
 const [dragging, setDragging] = useState<string | null>(null);
 const drag = useRef<Drag | null>(null); const before = useRef(new Map<string, DOMRect>()); const focusKey = useRef<string | null>(null);
 const current = useRef(keys); current.current = keys;
 const snapshot = () => { before.current = new Map(slots(grid.current).map(el => [el.dataset.panel!, el.getBoundingClientRect()])); };
 const follow = (el: HTMLElement, d: Drag) => {
  el.style.transform = ''; const natural = el.getBoundingClientRect();
  d.dx = d.x - d.grabX - natural.left; d.dy = d.y - d.grabY - natural.top; el.style.transform = `translate(${d.dx}px, ${d.dy}px)`;
 };
 const apply = (next: string[]) => { if (next.join('|') === current.current.join('|')) return; snapshot(); reorder(next); };

 useLayoutEffect(() => {
  const els = slots(grid.current); const motion = !reduced();
  for (const el of els) {
   const key = el.dataset.panel!; const d = drag.current;
   if (d?.active && d.key === key) { el.style.transition = 'none'; follow(el, d); continue; }
   const old = before.current.get(key); if (!old || !motion) continue;
   el.style.transition = 'none'; el.style.transform = ''; const now = el.getBoundingClientRect();
   const x = old.left - now.left, y = old.top - now.top; if (!x && !y) continue;
   el.style.transform = `translate(${x}px, ${y}px)`; void el.offsetWidth;
   el.style.transition = 'transform 260ms var(--ease-out-quart)'; el.style.transform = '';
  }
  before.current = new Map();
  if (focusKey.current) { grid.current?.querySelector<HTMLElement>(`[data-handle="${focusKey.current}"]`)?.focus(); focusKey.current = null; }
 }, [keys.join('|'), grid]);

 const hitTest = (d: Drag) => {
  const over = document.elementsFromPoint?.(d.x, d.y).map(e => e.closest<HTMLElement>('[data-panel]')).find(e => e && e.dataset.panel !== d.key && grid.current?.contains(e));
  const key = over?.dataset.panel;
  if (!key) { d.last = undefined; return; }
  if (key === d.last) return;
  d.last = key; apply(moveKey(current.current, d.key, current.current.indexOf(key)));
 };

 useEffect(() => {
  if (!dragging) return;
  let frame = 0;
  const tick = () => {
   const d = drag.current; if (!d) return;
   const speed = d.y < EDGE ? -(EDGE - d.y) / 3 : d.y > innerHeight - EDGE ? (d.y - innerHeight + EDGE) / 3 : 0;
   if (speed) { window.scrollBy(0, speed); hitTest(d); }
   const el = slots(grid.current).find(e => e.dataset.panel === d.key); if (el) follow(el, d);
   frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  const cancel = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape' && drag.current) { event.preventDefault(); const start = drag.current.start; finish(); apply(start); } };
  document.addEventListener('keydown', cancel);
  return () => { cancelAnimationFrame(frame); document.removeEventListener('keydown', cancel); };
 }, [dragging]);

 function finish() {
  const d = drag.current; drag.current = null; setDragging(null); if (!d) return;
  const el = slots(grid.current).find(e => e.dataset.panel === d.key); if (!el) return;
  if (reduced()) { el.style.transform = ''; return; }
  el.style.transition = 'transform 220ms var(--ease-out-quart)'; el.style.transform = '';
 }
 function start(key: string, event: PointerEvent<HTMLElement>) {
  if (drag.current || event.button !== 0 || (event.target as HTMLElement).closest('button:not([data-handle])')) return;
  const el = slots(grid.current).find(e => e.dataset.panel === key); if (!el) return;
  event.preventDefault(); event.currentTarget.setPointerCapture?.(event.pointerId);
  const rect = el.getBoundingClientRect();
  drag.current = { started: performance.now(), originX: event.clientX, originY: event.clientY, active: false, key, id: event.pointerId, grabX: event.clientX - rect.left, grabY: event.clientY - rect.top, x: event.clientX, y: event.clientY, dx: 0, dy: 0, start: current.current };

 }
 function move(event: PointerEvent<HTMLElement>) {
  const d = drag.current; if (!d || d.id !== event.pointerId) return;
  d.x = event.clientX; d.y = event.clientY;
  if (!d.active) {
   if (performance.now() - d.started < 150 || Math.hypot(d.x-d.originX, d.y-d.originY) < 8) return;
   d.active = true; setDragging(d.key);
  }
  hitTest(d);
 }
 function end(event: PointerEvent<HTMLElement>) {
  const d = drag.current; if (!d || d.id !== event.pointerId) return;
  const moved = d.start.join('|') !== current.current.join('|'); const key = d.key; finish();
  if (moved) describe(key, current.current.indexOf(key));
 }
 function key(panel: string, event: KeyboardEvent<HTMLElement>) {
  const index = current.current.indexOf(panel); const last = current.current.length - 1;
  const to = { ArrowUp: index - 1, ArrowLeft: index - 1, ArrowDown: index + 1, ArrowRight: index + 1, Home: 0, End: last }[event.key];
  if (to === undefined) return;
  event.preventDefault(); if (to < 0 || to > last || to === index) return;
  focusKey.current = panel; apply(moveKey(current.current, panel, to)); describe(panel, to);
 }
 function step(panel: string, delta: number) {
  const index=current.current.indexOf(panel); const to=index+delta;
  if(to<0 || to>=current.current.length)return;
  apply(moveKey(current.current,panel,to)); describe(panel,to);
 }
 return { dragging, start, move, end, key, step, keys };
}

type Reorder = ReturnType<typeof useReorder>;
export function EditablePanel({ panel, title, reorder, resize, hide, children }: { panel: HomePanel; title: string; reorder: Reorder; resize(size: HomePanel['size']): void; hide(): void; children: ReactNode }) {
 const lifted = reorder.dragging === panel.key;
 return <div data-panel={panel.key} className={`home-panel-slot home-panel-${panel.size} hp-editing${lifted ? ' hp-lifted' : ''}`}>
  <div className="hp-bar" onPointerDown={event => reorder.start(panel.key, event)} onPointerMove={reorder.move} onPointerUp={reorder.end} onPointerCancel={reorder.end}>
   <button type="button" className="hp-handle" data-handle={panel.key} aria-label={`Move ${title}`} aria-describedby="hp-move-help" onKeyDown={event => reorder.key(panel.key, event)}><GripVertical size={18} aria-hidden="true" /></button>
   <span className="hp-title">{title}</span>
   <button type="button" className="icon-button hp-hide" aria-label={`Move ${title} up`} disabled={reorder.keys[0]===panel.key} onClick={()=>reorder.step(panel.key,-1)}><ArrowUp size={17}/></button>
   <button type="button" className="icon-button hp-hide" aria-label={`Move ${title} down`} disabled={reorder.keys.at(-1)===panel.key} onClick={()=>reorder.step(panel.key,1)}><ArrowDown size={17}/></button>
   <div className="home-size hp-size" role="group" aria-label={`${title} width`}>{(['full', 'half'] as const).map(size => <button type="button" key={size} aria-pressed={panel.size === size} onClick={() => resize(size)}>{size === 'full' ? 'Full' : 'Half'}</button>)}</div>
   <button type="button" className="icon-button hp-hide" aria-label={`Hide ${title}`} title="Hide" onClick={hide}><EyeOff size={17} aria-hidden="true" /></button>
  </div>
  <div className="hp-body" inert>{children}</div>
 </div>;
}

export function EditBar({ busy, error, reset, cancel, done }: { busy: boolean; error?: unknown; reset(): void; cancel(): void; done(): void }) {
 return <div className="hp-editbar" role="region" aria-label="Editing Home">
  <div className="hp-editbar-text"><strong>Editing your Home</strong><span id="hp-move-help">Drag a panel by its bar, or focus its handle and use the arrow keys.</span></div>
  <div className="hp-editbar-actions"><button type="button" className="text-button" disabled={busy} onClick={reset}>Reset to default</button><button type="button" className="button secondary" disabled={busy} onClick={cancel}>Cancel</button><button type="button" className="button primary" disabled={busy} onClick={done}>{busy ? 'Saving…' : 'Save'}</button></div>
  {!!error && <ErrorNotice error={error} />}
 </div>;
}

export function HiddenPanels({ hidden, add }: { hidden: HomePanelOption[]; add(key: string): void }) {
 if (!hidden.length) return null;
 return <section className="hp-shelf" aria-label="Hidden panels"><h2>Hidden panels</h2><div className="hp-shelf-list">{hidden.map(panel => <button type="button" key={panel.key} className="hp-shelf-item" onClick={() => add(panel.key)}><Plus size={16} aria-hidden="true" />Add {panel.title}</button>)}</div></section>;
}
